import React from 'react';
import { colors, fonts } from './styles';

export const HeroSection = ({ bannerOffset }) => {
  const topPad = (bannerOffset || 0) + 120;
  return (
    <section
      style={{
        backgroundColor: colors.offWhite,
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: `${topPad}px 48px 96px`,
        position: 'relative',
        overflow: 'hidden',
        boxSizing: 'border-box',
      }}
    >
      <style>{`
        @keyframes pi-fadeUp { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes pi-hl-in { from { transform: scaleX(0); } to { transform: scaleX(1); } }
        .pi-hero-eyebrow  { animation: pi-fadeUp 0.6s ease both; }
        .pi-hero-headline { animation: pi-fadeUp 0.6s ease 0.1s both; }
        .pi-hero-sub      { animation: pi-fadeUp 0.6s ease 0.2s both; }
        .pi-hero-attrs    { animation: pi-fadeUp 0.6s ease 0.35s both; }
        .pi-hl { position: relative; display: inline-block; padding: 0 1px; }
        .pi-hl::before {
          content: '';
          position: absolute;
          left: 0;
          right: 0;
          bottom: 8%;
          height: 36%;
          background: linear-gradient(90deg, rgba(235,160,155,0.55) 0%, rgba(245,185,165,0.45) 100%);
          transform: scaleX(0);
          transform-origin: left center;
          border-radius: 2px;
          z-index: -1;
          animation: pi-hl-in 0.8s cubic-bezier(0.22, 1, 0.36, 1) 0.7s forwards;
        }
        @media (max-width: 768px) {
          .pi-hero-section { padding-left: 24px !important; padding-right: 24px !important; }
          .pi-hero-attrs { gap: 24px !important; }
          .pi-hero-attr-value { max-width: none !important; }
        }
      `}</style>

      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: '-240px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '1100px',
          height: '700px',
          background: 'radial-gradient(ellipse, rgba(235,160,155,0.28) 0%, transparent 65%)',
          pointerEvents: 'none',
        }}
      />
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          bottom: '-200px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '900px',
          height: '500px',
          background: 'radial-gradient(ellipse, rgba(245,185,165,0.22) 0%, transparent 65%)',
          pointerEvents: 'none',
        }}
      />

      <div
        className="pi-hero-section"
        style={{
          maxWidth: '860px',
          width: '100%',
          position: 'relative',
          zIndex: 1,
          textAlign: 'center',
        }}
      >
        <p
          className="pi-hero-eyebrow"
          style={{
            fontFamily: fonts.sans,
            fontSize: '11px',
            fontWeight: 600,
            letterSpacing: '2px',
            textTransform: 'uppercase',
            color: colors.deepCoral,
            margin: '0 0 32px',
          }}
        >
          On-Demand Patient Intelligence<sup style={{ fontSize: '0.7em', verticalAlign: 'super' }}>TM</sup>
        </p>
        <h1
          className="pi-hero-headline"
          style={{
            fontFamily: fonts.serif,
            fontSize: 'clamp(42px, 5.5vw, 72px)',
            fontWeight: 700,
            lineHeight: 1.08,
            color: colors.charcoal,
            margin: '0 0 24px',
          }}
        >
          Most patient data captures a moment.
          <br />
          <em style={{ fontStyle: 'italic', color: colors.charcoal, fontWeight: 700 }}>
            Ours captures a <span className="pi-hl">journey.</span>
          </em>
        </h1>
        <p
          className="pi-hero-sub"
          style={{
            fontFamily: fonts.sans,
            fontSize: '18px',
            lineHeight: 1.65,
            color: colors.warmGray,
            maxWidth: '640px',
            margin: '0 auto 20px',
            fontWeight: 400,
          }}
        >
          We've spent a decade building relationships with patient communities<br className="pi-br-md" />
          {' '}across rare, complex, and high-need conditions.
        </p>
        <p
          className="pi-hero-sub"
          style={{
            fontFamily: fonts.sans,
            fontSize: '19px',
            lineHeight: 1.55,
            color: colors.charcoal,
            maxWidth: '640px',
            margin: '0 auto 56px',
            fontWeight: 600,
          }}
        >
          Now your team can get answers from real patients in days.
        </p>
        <style>{`
          .pi-br-md { display: none; }
          @media (min-width: 768px) { .pi-br-md { display: inline; } }
        `}</style>
        <div
          className="pi-hero-attrs"
          style={{
            display: 'flex',
            gap: '48px',
            flexWrap: 'wrap',
            justifyContent: 'center',
            borderTop: `1px solid ${colors.border}`,
            paddingTop: '40px',
          }}
        >
          {[
            { label: 'Verified', value: 'Identity and diagnosis confirmed — not proxies or panel inferences' },
            { label: 'On-Demand', value: 'Real-time signal, available whenever a question arises' },
            { label: 'Longitudinal', value: 'Intelligence that compounds and deepens with every engagement' },
          ].map((attr) => (
            <div
              key={attr.label}
              style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'center', textAlign: 'center' }}
            >
              <span
                style={{
                  fontFamily: fonts.sans,
                  fontSize: '10px',
                  fontWeight: 600,
                  letterSpacing: '1.5px',
                  textTransform: 'uppercase',
                  color: colors.deepCoral,
                }}
              >
                {attr.label}
              </span>
              <span
                className="pi-hero-attr-value"
                style={{
                  fontFamily: fonts.sans,
                  fontSize: '14px',
                  color: colors.warmGray,
                  fontWeight: 400,
                  maxWidth: '200px',
                  lineHeight: 1.5,
                }}
              >
                {attr.value}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
